<script setup lang="ts">
import { useSchoolStudents } from '@/composables/student/useSchoolStudents';
import { Components } from '@/api/generated/api';
import { useSchoolClassAccess } from '@/composables/schoolClassAccess/useSchoolClassAccess';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import { useSchoolSubjectGroup } from '@/composables/schoolSubjectGroups/useSchoolSubjectGroup';
import { IConfirmDialogProps, useConfirmDialog } from '@/composables/useConfirmDialog';
import { useDirtySnapshot } from '@/composables/useDirtySnapshot';
import { useSchoolClasses } from '@/composables/useSchoolClasses';
import { useToast } from '@/composables/useToast';
import ArchiveClassOverviewStep from '@/core/dialogs/archive-class/ArchiveClassOverviewStep.vue';
import ArchiveClassTeamStep from '@/core/dialogs/archive-class/ArchiveClassTeamStep.vue';
import DialogBase from '@/core/dialogs/base-dialog/DialogBase.vue';
import ClassFormFooter, { IClassFormFooterConfig } from '@/core/dialogs/create-class/ClassFormFooter.vue';
import { IUserWithPermissions } from '@/interfaces';
import { useContainerTranslations } from '@/schoolClasses/useContainerTranslations';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { assertArrayNotEmpty, assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { toRecord } from 'common/utils/array.utils';
import { getClassName } from 'common/utils/format.utils';

const { t } = useI18n();
const {
  getSchoolClassAccessInvitationsAndUsers,
  refresh: refreshAccessRights,
  upsertAnyAccessRights,
} = useSchoolClassAccess();
const { getSchoolSubjectsForClass } = useSchoolSubject();
const { getSchoolSubjectGroupsForClass } = useSchoolSubjectGroup();
const isOpen = ref(false);
const { allStudentsAdmin, refresh: refreshSchoolStudents } = useSchoolStudents(isOpen);
const { confirm } = useConfirmDialog();
const { customErrorToast, customSuccessToast } = useToast();
const { archiveSchoolClassesBulk } = useSchoolClasses();
const { setCurrentClass } = useCurrentClass();

enum EArchiveClassStep {
  OVERVIEW = 'OVERVIEW',
  TEAM = 'TEAM',
}

type TArchiveDialogMode = 'single' | 'bulk';

interface IArchiveDialogResult {
  archivedClassIds: number[];
  failedClassIds: number[];
}

interface IArchiveClassConfirmConfig {
  confirmButtonLabel: string;
  description: string;
  title: string;
}

type TArchiveDialogShowParams = { classesOrGroups: Components.Schemas.SchoolClassDto[] };

const dialogRef = useTemplateRef('dialogRef');
const overviewStepRef = useTemplateRef('overviewStepRef');
const teamStepRef = useTemplateRef('teamStepRef');

const emit = defineEmits<{
  archived: [result: IArchiveDialogResult];
}>();

const dialogMode = ref<TArchiveDialogMode>('single');
const classesOrGroupsBulk = ref<Components.Schemas.SchoolClassDto[]>([]);
const bulkClassIndex = ref(0);
const finalNotesByClassId = ref<Record<number, string>>({});
const usersWithAccessByClassId = ref<Record<number, IUserWithPermissions[]>>({});
const hasTeamChangesByClassId = ref<Record<number, boolean>>({});
const isTeamLoadedByClassId = ref<Record<number, boolean>>({});
const isSubmitting = ref(false);
const currentStep = ref<EArchiveClassStep>(EArchiveClassStep.OVERVIEW);
const schoolClass = ref<Components.Schemas.SchoolClassDto | null>(null);
const finalNote = ref<string>('');
const usersWithAccess = ref<IUserWithPermissions[]>([]);
const schoolSubjects = ref<Components.Schemas.SchoolSubjectDto[]>([]);
const schoolSubjectGroups = ref<Components.Schemas.SchoolSubjectGroupDto[]>([]);
const students = ref<Components.Schemas.StudentDto[]>([]);
const hasTeamChanges = ref(false);

const { captureSnapshot, isDirty } = useDirtySnapshot(
  () => ({ finalNotesByClassId: finalNotesByClassId.value }),
  currentStep,
);

const isBulkMode = computed<boolean>(() => dialogMode.value === 'bulk');
// bulk archiving spans many classes, so its own wording is used before any of these are reached
const { tContainer } = useContainerTranslations(schoolClass);
const bulkClassCount = computed<number>(() => classesOrGroupsBulk.value.length);
const isLastClassInFlow = computed<boolean>(() => {
  if (!isBulkMode.value) return true;
  return bulkClassIndex.value === bulkClassCount.value - 1;
});
const isFirstClassInFlow = computed<boolean>(() => {
  if (!isBulkMode.value) return true;
  return bulkClassIndex.value === 0;
});
const dialogTitle = computed<string>(() => {
  if (isBulkMode.value) {
    return t('classes.archiveBulkTitle', { count: bulkClassCount.value });
  }
  return tContainer('archive');
});
const bulkCurrentClassLabel = computed<string>(() => {
  if (!schoolClass.value) return '';
  return t('classes.archiveBulkCurrentClass', { className: getClassName(schoolClass.value) });
});
const bulkStepLabel = computed<string>(() => {
  return t('classes.archiveBulkStep', { x: bulkClassIndex.value + 1, y: bulkClassCount.value });
});
const archiveConfirmButtonLabel = computed<string>(() => {
  if (isBulkMode.value) {
    return t('classes.archiveBulkAction', { count: bulkClassCount.value });
  }
  return tContainer('archive');
});

const reset = (): void => {
  isOpen.value = false;
  dialogMode.value = 'single';
  classesOrGroupsBulk.value = [];
  bulkClassIndex.value = 0;
  finalNotesByClassId.value = {};
  usersWithAccessByClassId.value = {};
  hasTeamChangesByClassId.value = {};
  isTeamLoadedByClassId.value = {};
  isSubmitting.value = false;
  currentStep.value = EArchiveClassStep.OVERVIEW;
  schoolClass.value = null;
  finalNote.value = '';
  usersWithAccess.value = [];
  schoolSubjects.value = [];
  schoolSubjectGroups.value = [];
  students.value = [];
  hasTeamChanges.value = false;
};

const discardChangesConfirmConfig = computed<IConfirmDialogProps>(() => ({
  title: t('general.cancelEntry'),
  description: t('general.cancelEntryDescription'),
  declineButtonLabel: t('general.continueEditing'),
  confirmButtonLabel: t('general.cancelEntry'),
  showConfirmOn: () => isBulkMode.value || isDirty(),
}));

const footerConfig = computed<IClassFormFooterConfig>(() => {
  if (currentStep.value === EArchiveClassStep.OVERVIEW) {
    return {
      helpText: !isBulkMode.value ? t('classes.archiveClassTeamDescription') : undefined,
      previousBtnLabel: isSubmitting.value || isFirstClassInFlow.value ? undefined : t('general.back'),
      nextBtnLabel: t('general.continue'),
    };
  }

  let nextBtnLabel = t('general.continue');
  if (isSubmitting.value) {
    if (isBulkMode.value) nextBtnLabel = t('classes.archiveBulkInProgress');
    else nextBtnLabel = tContainer('archiveInProgress');
  } else if (isLastClassInFlow.value) {
    nextBtnLabel = archiveConfirmButtonLabel.value;
  }

  return {
    previousBtnLabel: isSubmitting.value ? undefined : t('general.back'),
    nextBtnLabel,
  };
});

const moveToBulkClassStep = async (index: number, step: EArchiveClassStep): Promise<void> => {
  const nextClass = classesOrGroupsBulk.value[index];
  assertNotNullOrUndefined(nextClass);

  bulkClassIndex.value = index;
  schoolClass.value = nextClass;
  currentStep.value = step;
  await updateData();
};

const getCurrentSchoolClass = (): Components.Schemas.SchoolClassDto => {
  const currentSchoolClass = schoolClass.value;
  assertNotNullOrUndefined(currentSchoolClass?.id);
  return currentSchoolClass;
};

const getClassesToProcess = (
  currentSchoolClass: Components.Schemas.SchoolClassDto,
): Components.Schemas.SchoolClassDto[] => {
  return isBulkMode.value ? classesOrGroupsBulk.value : [currentSchoolClass];
};

const getArchiveClassConfirmConfig = (): IArchiveClassConfirmConfig => {
  if (isBulkMode.value) {
    return {
      confirmButtonLabel: archiveConfirmButtonLabel.value,
      description: t('classes.archiveBulkConfirmDescription', { count: bulkClassCount.value }),
      title: t('classes.archiveBulkTitle', { count: bulkClassCount.value }),
    };
  }

  return {
    confirmButtonLabel: tContainer('archiveConfirmButton'),
    description: tContainer('archiveConfirmDescription'),
    title: tContainer('archive'),
  };
};

const showPreviousStep = async (): Promise<void> => {
  if (isSubmitting.value) return;

  if (currentStep.value === EArchiveClassStep.TEAM) {
    currentStep.value = EArchiveClassStep.OVERVIEW;
    return;
  }

  if (!isBulkMode.value || bulkClassIndex.value === 0) return;
  await moveToBulkClassStep(bulkClassIndex.value - 1, EArchiveClassStep.TEAM);
};

const updateFinalNote = (note: string): void => {
  if (!schoolClass.value?.id) return;

  finalNotesByClassId.value[schoolClass.value.id] = note;
  finalNote.value = note;
};

const handleGeneralDataStepDone = async (): Promise<void> => {
  currentStep.value = EArchiveClassStep.TEAM;
};

const handleTeamStepDone = async (): Promise<void> => {
  if (!isLastClassInFlow.value && isBulkMode.value) {
    await moveToBulkClassStep(bulkClassIndex.value + 1, EArchiveClassStep.OVERVIEW);
    return;
  }

  await handleFinalizeStepDone();
};

const handleFinalizeStepDone = async (): Promise<void> => {
  if (isSubmitting.value) return;

  const currentSchoolClass = getCurrentSchoolClass();
  const confirmConfig = getArchiveClassConfirmConfig();

  const confirmed = await confirm({
    confirmButtonLabel: confirmConfig.confirmButtonLabel,
    declineButtonLabel: t('general.cancel'),
    description: confirmConfig.description,
    title: confirmConfig.title,
  });
  if (!confirmed) return;

  const classesToProcess = getClassesToProcess(currentSchoolClass);
  if (!classesToProcess.length) return;

  isSubmitting.value = true;
  try {
    const accessFailedClassIds: number[] = [];
    const classesToArchive: { schoolClassId: number; finalNote: string }[] = [];

    for (const classToProcess of classesToProcess) {
      const usersForClass = usersWithAccessByClassId.value[classToProcess.id];
      const hasTeamChangesForClass = hasTeamChangesByClassId.value[classToProcess.id];

      if (hasTeamChangesForClass) {
        try {
          await upsertAnyAccessRights(classToProcess.id, usersForClass);
        } catch {
          accessFailedClassIds.push(classToProcess.id);
          continue;
        }
      }

      classesToArchive.push({
        schoolClassId: classToProcess.id,
        finalNote: finalNotesByClassId.value[classToProcess.id] ?? '',
      });
    }

    const { archivedClassIds, failedClassIds: archiveFailedClassIds } =
      await archiveSchoolClassesBulk(classesToArchive);
    const allFailedClassIds = Array.from(new Set([...accessFailedClassIds, ...archiveFailedClassIds]));

    if (allFailedClassIds.length > 0) {
      const failedClassNames = classesToProcess
        .filter((item) => allFailedClassIds.includes(item.id))
        .map((item) => getClassName(item))
        .join(', ');
      customErrorToast(t('classes.archiveBulkPartialFailure', { classNames: failedClassNames }));
    } else {
      customSuccessToast(
        isBulkMode.value
          ? t('classes.archiveBulkSuccess', { count: archivedClassIds.length })
          : t('general.editSuccess'),
      );
    }

    if (!isBulkMode.value && archivedClassIds.includes(currentSchoolClass.id)) {
      setCurrentClass(currentSchoolClass);
    }
    emit('archived', { archivedClassIds, failedClassIds: allFailedClassIds });
    dialogRef.value?.hide();
  } catch {
    customErrorToast(t('errorMessages.networkError'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async (): Promise<void> => {
  if (isSubmitting.value) return;

  if (currentStep.value === EArchiveClassStep.OVERVIEW) {
    overviewStepRef.value?.onSubmit();
    return;
  }

  teamStepRef.value?.onSubmit();
};

const updateUsersWithAccess = (team: IUserWithPermissions[]): void => {
  if (!schoolClass.value?.id) return;

  usersWithAccess.value = team;
  usersWithAccessByClassId.value[schoolClass.value.id] = team;
  hasTeamChanges.value = true;
  hasTeamChangesByClassId.value[schoolClass.value.id] = true;
};

const updateData = async (): Promise<void> => {
  if (!schoolClass.value?.id) return;
  const schoolClassId = schoolClass.value.id;

  finalNote.value = finalNotesByClassId.value[schoolClassId] ?? '';
  students.value = allStudentsAdmin.value.filter((student) =>
    student.schoolClasses.some((sc) => sc.id === schoolClassId),
  );

  [schoolSubjects.value, schoolSubjectGroups.value] = await Promise.all([
    getSchoolSubjectsForClass(schoolClassId),
    getSchoolSubjectGroupsForClass(schoolClassId),
  ]);

  if (isTeamLoadedByClassId.value[schoolClassId]) {
    usersWithAccess.value = usersWithAccessByClassId.value[schoolClassId];
    hasTeamChanges.value = hasTeamChangesByClassId.value[schoolClassId];
    return;
  }

  const initialTeam = getSchoolClassAccessInvitationsAndUsers(schoolClassId);

  usersWithAccess.value = initialTeam;
  usersWithAccessByClassId.value[schoolClassId] = initialTeam;
  hasTeamChanges.value = false;
  hasTeamChangesByClassId.value[schoolClassId] = false;
  isTeamLoadedByClassId.value[schoolClassId] = true;
};

const show = async (params: TArchiveDialogShowParams): Promise<void> => {
  reset();
  isOpen.value = true;
  try {
    // a changed team is saved as a whole, so it has to start from the latest access rights
    await Promise.all([refreshSchoolStudents(), refreshAccessRights()]);
  } catch (error) {
    isOpen.value = false;
    throw error;
  }

  const { classesOrGroups } = params;
  const nonEmptyClassesOrGroups = assertArrayNotEmpty(classesOrGroups);

  dialogMode.value = classesOrGroups.length > 1 ? 'bulk' : 'single';
  classesOrGroupsBulk.value = dialogMode.value === 'bulk' ? classesOrGroups : [];
  schoolClass.value = nonEmptyClassesOrGroups[0];
  bulkClassIndex.value = 0;
  finalNotesByClassId.value = toRecord(
    classesOrGroups,
    (schoolClassItem) => schoolClassItem.id,
    () => '',
  );
  usersWithAccessByClassId.value = toRecord(
    classesOrGroups,
    (schoolClassItem) => schoolClassItem.id,
    () => [],
  );
  hasTeamChangesByClassId.value = toRecord(
    classesOrGroups,
    (schoolClassItem) => schoolClassItem.id,
    () => false,
  );
  isTeamLoadedByClassId.value = toRecord(
    classesOrGroups,
    (schoolClassItem) => schoolClassItem.id,
    () => false,
  );

  currentStep.value = EArchiveClassStep.OVERVIEW;
  await updateData();
  dialogRef.value?.show();
  await nextTick();
  captureSnapshot();
};

defineExpose({
  show,
});
</script>

<template>
  <DialogBase
    ref="dialogRef"
    :title="dialogTitle"
    :confirm-config="discardChangesConfirmConfig"
    fill-height
    no-padding
    :disable-internal-hide-logic="isSubmitting"
    :show-close-button="!isSubmitting"
    @hide="reset"
  >
    <template v-if="isBulkMode && bulkClassCount > 1 && schoolClass" #above-header>
      <div class="bg-primary/10 px-content py-lg flex items-center text-tint">
        <span class="font-semibold">{{ bulkCurrentClassLabel }}</span>
        <span class="ml-auto">{{ bulkStepLabel }}</span>
      </div>
    </template>
    <ArchiveClassOverviewStep
      v-if="currentStep === EArchiveClassStep.OVERVIEW"
      ref="overviewStepRef"
      :school-class="schoolClass"
      :students="students"
      :show-description="!isBulkMode"
      :allow-final-note="true"
      :final-note="finalNote"
      @update:final-note="updateFinalNote"
      @continue="handleGeneralDataStepDone"
    />
    <ArchiveClassTeamStep
      v-if="currentStep === EArchiveClassStep.TEAM"
      ref="teamStepRef"
      :school-class="schoolClass"
      :available-school-subjects="schoolSubjects"
      :available-school-subject-groups="schoolSubjectGroups"
      :show-description="!isBulkMode"
      :users-with-access="usersWithAccess"
      @continue="handleTeamStepDone"
      @update:users-with-access="updateUsersWithAccess"
    />
    <template #footer>
      <ClassFormFooter
        :footer-config="footerConfig"
        :form-valid="!isSubmitting"
        @click:next="handleSubmit"
        @click:previous="showPreviousStep"
      />
    </template>
  </DialogBase>
</template>
